import cx from 'classnames';
import { find, isNumber } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Math } from 'classes';
import { CopyText, Dialog } from 'components';
import { AssetChangeReason, AssetType } from 'enums';
import { useCurrencies, useOrdersContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AssetHistoryRelation } from 'types';
import { assetBalanceHistoryUtils } from 'utils';

export type AmountErrorDialogData = AssetHistoryRelation & {
  assetId: string;
  assetType: AssetType;
  assetCurrencyId: string;
  userId?: string;
  shopId?: string;
  balances: { balance: number; holdBalance: number };
  updates: { balance: number; holdBalance: number };
  assetChangeReason: AssetChangeReason;
};

type Props = {
  open: boolean;
  onClose: () => void;
  data?: AmountErrorDialogData;
};

export const AmountErrorDialog: React.FC<Props> = ({
  open,
  onClose,
  data = {} as AmountErrorDialogData,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.asset_balance_history',
  });
  const { t: tCommon } = useTranslation();

  const { getAssetCurrencyCode } = useCurrencies();

  const { shops, merchants, traders } = useOrdersContext();
  const user = useMemo(
    () =>
      find(traders, { id: data.userId }) ||
      find(merchants, { id: data.userId }),
    [merchants, traders, data.userId],
  );
  const shop = useMemo(
    () => find(shops, { id: data.shopId }),
    [shops, data.shopId],
  );

  const isBalanceError = useMemo(
    () =>
      new Math(data.balances?.balance || 0).plus(data.updates?.balance || 0)
        .value < 0,
    [data.balances?.balance, data.updates?.balance],
  );
  const isHoldBalanceError = useMemo(
    () =>
      new Math(data.balances?.holdBalance || 0).plus(
        data.updates?.holdBalance || 0,
      ).value < 0,
    [data.balances?.holdBalance, data.updates?.holdBalance],
  );

  return (
    <Dialog
      title={tCommon('errors.not_enough_money')}
      open={open}
      onClose={onClose}
      hideCancel
    >
      <div>
        {data.userId && (
          <div className="tw-mb-2">
            <div>
              <b>{`${t('columns.user')}: `}</b>
            </div>
            <div>{user?.name}</div>
            <CopyText text={data.userId} />
          </div>
        )}
        {data.shopId && (
          <div className="tw-mb-2">
            <div>
              <b>{`${t('columns.shop')}: `}</b>
            </div>
            <div>{shop?.name}</div>
            <CopyText text={data.shopId} />
          </div>
        )}
        {data.assetId && (
          <div className="tw-mb-2">
            <div>
              <b>{`${t('columns.asset')}: `}</b>
            </div>
            {data.assetType && (
              <div>
                {tCommon(`assets.type.${data.assetType}`, {
                  defaultValue: data.assetType,
                })}
                {` ${getAssetCurrencyCode(data.assetCurrencyId)}`}
              </div>
            )}
            <CopyText text={data.assetId} />
            {isNumber(data.balances?.balance) && (
              <div className={cx({ 'tw-text-red-500': isBalanceError })}>{`${t(
                'columns.balance',
              )}: ${data.balances.balance}`}</div>
            )}
            {isNumber(data.balances?.holdBalance) && (
              <div
                className={cx({ 'tw-text-red-500': isHoldBalanceError })}
              >{`${t('columns.hold_balance')}: ${
                data.balances.holdBalance
              }`}</div>
            )}
          </div>
        )}
        {data.updates && (
          <div className="tw-mb-2">
            <b>{`${t('columns.reason')}: `}</b>
            <div>
              {assetBalanceHistoryUtils.getAssetChangeReasonLabel(
                data.assetChangeReason,
              )}
            </div>
            {data.orderId && (
              <div>
                {`${t('filters.order_id')}: `}
                <CopyText text={data.orderId} />
              </div>
            )}
            {data.payoutOrderId && (
              <div>
                {`${t('filters.payout_order_id')}: `}
                <CopyText text={data.payoutOrderId} />
              </div>
            )}
            {isNumber(data.updates.balance) && (
              <div className={cx({ 'tw-text-red-500': isBalanceError })}>{`${t(
                'columns.balance',
              )}: ${data.updates.balance}`}</div>
            )}
            {isNumber(data.updates.holdBalance) && (
              <div
                className={cx({ 'tw-text-red-500': isHoldBalanceError })}
              >{`${t('columns.hold_balance')}: ${
                data.updates.holdBalance
              }`}</div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
